import { LitElement, html, svg, css } from 'lit';

/**
 * CrusherSparkline
 *
 * Opinionated minimal chart — single line / area / dots, no axes, no
 * legend, no tooltips. Designed for inline "trend at a glance" use cases
 * like CloudAgencyOps's rolling-6-month cost chart, dashboard metric cards,
 * activity timelines, etc.
 *
 * Explicitly NOT a chart library. If a consumer needs axes, grids, multiple
 * series, tooltips on hover, or any interactivity beyond a hover-tone hint,
 * use a real chart library (Recharts, Chart.js, Apache ECharts) — this
 * primitive intentionally stays out of that territory.
 *
 * Properties:
 *   - data       (Array of numbers) — the values to plot. Empty array renders a flat baseline.
 *   - labels     (Array of strings, optional, same length as `data`) — used for the
 *                aria-label of each dot (e.g., month names: ["Jan","Feb",...])
 *   - width      (number) — SVG viewport width (default 200)
 *   - height     (number) — SVG viewport height (default 48)
 *   - tone       (enum) — "brand" (default) | "success" | "warning" | "danger" | "info" | "neutral"
 *                Maps to feedback / brand semantic vars.
 *   - show-area  (boolean) — fill under the line with a soft brand-tinted area
 *   - show-dots  (boolean) — render small dots at each data point
 *   - show-first-last (boolean) — render dots ONLY at first + last data point (cheaper than show-dots when the
 *                     series is long; useful for endpoint emphasis)
 *   - stroke-width (number) — line stroke width in SVG units (default 2)
 *   - aria-label (inherited) — overrides the auto-derived accessible name
 *
 * A11y: rendered as <svg role="img"> with an auto-derived aria-label
 * summarizing min/max/last/delta (or the consumer's override).
 *
 * No events; this is a display primitive.
 */
export class CrusherSparkline extends LitElement {
  static properties = {
    data:           { type: Array },
    labels:         { type: Array },
    width:          { type: Number, reflect: true },
    height:         { type: Number, reflect: true },
    tone:           { type: String, reflect: true },
    showArea:       { type: Boolean, attribute: 'show-area', reflect: true },
    showDots:       { type: Boolean, attribute: 'show-dots', reflect: true },
    showFirstLast:  { type: Boolean, attribute: 'show-first-last', reflect: true },
    strokeWidth:    { type: Number, attribute: 'stroke-width', reflect: true }
  };

  constructor() {
    super();
    this.data = [];
    this.labels = [];
    this.width = 200;
    this.height = 48;
    this.tone = 'brand';
    this.showArea = false;
    this.showDots = false;
    this.showFirstLast = false;
    this.strokeWidth = 2;
  }

  static styles = css`
    :host {
      display: inline-block;
      line-height: 0;
    }
    svg {
      display: block;
      overflow: visible;
    }

    .stroke[data-tone="brand"]    { stroke: var(--crusher-color-brand-primary); }
    .stroke[data-tone="success"]  { stroke: var(--crusher-feedback-success); }
    .stroke[data-tone="warning"]  { stroke: var(--crusher-feedback-warning); }
    .stroke[data-tone="danger"]   { stroke: var(--crusher-feedback-danger); }
    .stroke[data-tone="info"]     { stroke: var(--crusher-feedback-info); }
    .stroke[data-tone="neutral"]  { stroke: var(--crusher-fg-muted); }

    .fill[data-tone="brand"]      { fill: color-mix(in srgb, var(--crusher-color-brand-primary) 12%, transparent); }
    .fill[data-tone="success"]    { fill: color-mix(in srgb, var(--crusher-feedback-success) 12%, transparent); }
    .fill[data-tone="warning"]    { fill: color-mix(in srgb, var(--crusher-feedback-warning) 12%, transparent); }
    .fill[data-tone="danger"]     { fill: color-mix(in srgb, var(--crusher-feedback-danger) 12%, transparent); }
    .fill[data-tone="info"]       { fill: color-mix(in srgb, var(--crusher-feedback-info) 12%, transparent); }
    .fill[data-tone="neutral"]    { fill: color-mix(in srgb, var(--crusher-fg) 8%, transparent); }

    .dot[data-tone="brand"]    { fill: var(--crusher-color-brand-primary); }
    .dot[data-tone="success"]  { fill: var(--crusher-feedback-success); }
    .dot[data-tone="warning"]  { fill: var(--crusher-feedback-warning); }
    .dot[data-tone="danger"]   { fill: var(--crusher-feedback-danger); }
    .dot[data-tone="info"]     { fill: var(--crusher-feedback-info); }
    .dot[data-tone="neutral"]  { fill: var(--crusher-fg-muted); }
  `;

  _toneOrFallback() {
    const allowed = new Set(['brand', 'success', 'warning', 'danger', 'info', 'neutral']);
    return allowed.has(this.tone) ? this.tone : 'brand';
  }

  /* Compute SVG coordinates from the data array. We leave 2 SVG units of
     padding on each side so dots at the endpoints don't get clipped. */
  _computePoints() {
    const data = Array.isArray(this.data) ? this.data : [];
    if (data.length === 0) return [];
    const pad = 2;
    const w = this.width - pad * 2;
    const h = this.height - pad * 2;

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    if (data.length === 1) {
      return [{ x: this.width / 2, y: this.height / 2, value: data[0], index: 0 }];
    }

    const step = w / (data.length - 1);
    return data.map((value, i) => ({
      x: pad + i * step,
      /* SVG y-axis points DOWN. Invert so larger values render UP. */
      y: pad + h - ((value - min) / range) * h,
      value,
      index: i
    }));
  }

  _autoAriaLabel() {
    const data = Array.isArray(this.data) ? this.data : [];
    if (data.length === 0) return 'No data';
    if (data.length === 1) return `Single value ${data[0]}`;
    const first = data[0];
    const last = data[data.length - 1];
    const delta = last - first;
    const dir = delta > 0 ? 'up' : (delta < 0 ? 'down' : 'flat');
    const sign = delta > 0 ? '+' : '';
    return `Trend ${dir} from ${first} to ${last} (${sign}${delta}) across ${data.length} points`;
  }

  _consumerAriaLabel() {
    return this.getAttribute('aria-label') ?? this._autoAriaLabel();
  }

  render() {
    const tone = this._toneOrFallback();
    const points = this._computePoints();

    if (points.length === 0) {
      /* Render a flat baseline so the sparkline doesn't disappear when data
         is empty; a horizontal line at vertical center conveys "no trend". */
      return html`
        <svg viewBox=${`0 0 ${this.width} ${this.height}`} width=${this.width} height=${this.height} role="img" aria-label="No data">
          <line
            x1="2" y1=${this.height / 2}
            x2=${this.width - 2} y2=${this.height / 2}
            class="stroke" data-tone="neutral"
            stroke-width=${this.strokeWidth}
            stroke-linecap="round" />
        </svg>
      `;
    }

    const polyPoints = points.map((p) => `${p.x},${p.y}`).join(' ');
    /* Area uses the polyline plus a baseline at the SVG bottom (height) */
    const areaPoints = `${points[0].x},${this.height - 2} ${polyPoints} ${points[points.length - 1].x},${this.height - 2}`;

    const showDots = this.showDots;
    const showFirstLast = this.showFirstLast && !this.showDots;

    return html`
      <svg viewBox=${`0 0 ${this.width} ${this.height}`} width=${this.width} height=${this.height} role="img" aria-label=${this._consumerAriaLabel()}>
        ${this.showArea ? svg`
          <polygon class="fill" data-tone=${tone} points=${areaPoints} />
        ` : null}
        <polyline
          class="stroke" data-tone=${tone}
          fill="none"
          stroke-width=${this.strokeWidth}
          stroke-linejoin="round"
          stroke-linecap="round"
          points=${polyPoints} />
        ${showDots ? points.map((p) => svg`
          <circle class="dot" data-tone=${tone} cx=${p.x} cy=${p.y} r=${Math.max(2, this.strokeWidth)} />
        `) : null}
        ${showFirstLast ? [points[0], points[points.length - 1]].map((p) => svg`
          <circle class="dot" data-tone=${tone} cx=${p.x} cy=${p.y} r=${Math.max(2.5, this.strokeWidth + 0.5)} />
        `) : null}
      </svg>
    `;
  }
}

customElements.define('crusher-sparkline', CrusherSparkline);
